import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import DefaultNavbarLayout from "layouts/defaultNavbar";
import Footer from "layouts/footer";

function AdminDashboard() {
  const [data, setData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [originData, setOriginData] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [credentials, setCredentials] = useState(null);
  
  // Filtros de fecha - por defecto mes actual
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth() -3, 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(lastDayOfMonth.toISOString().split('T')[0]);

  useEffect(() => {
    // Solicitar credenciales al cargar el componente
    requestCredentials();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestCredentials = () => {
    const username = prompt('Usuario:', '');
    const password = prompt('Contraseña:', '');

    console.log('=== DEBUG CREDENCIALES ===');
    console.log('Usuario ingresado:', username);
    console.log('Password ingresado:', password);

    if (!username || !password) {
      setError('❌ Credenciales requeridas para acceder al dashboard');
      setLoading(false);
      return;
    }

    const encodedCredentials = btoa(`${username}:${password}`);
    console.log('Credenciales codificadas:', encodedCredentials);
    console.log('Credenciales decodificadas (verificación):', atob(encodedCredentials));

    setCredentials(encodedCredentials);
    fetchStats(encodedCredentials);
  };

  const fetchStats = async (authCredentials = credentials, fromDate = dateFrom, toDate = dateTo) => {
    if (!authCredentials) {
      setError('❌ No hay credenciales disponibles');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('=== DEBUG FETCH ===');
      console.log('Credenciales a enviar:', authCredentials);
      console.log('Fechas:', { from: fromDate, to: toDate });

      // Decodificar credenciales para obtener usuario y password por separado
      const decoded = atob(authCredentials);
      const [username, password] = decoded.split(':');

      console.log('Usuario decodificado:', username);
      console.log('Password decodificado:', password);

      // MÉTODO 1: Intentar con headers X-Auth (FUNCIONA EN PRODUCCIÓN)
      const params = new URLSearchParams({
        date_from: fromDate,
        date_to: toDate
      });

      let response = await fetch(`https://shouer.com.ar/api-stats.php?${params}`, {
        method: 'GET',
        headers: {
          'X-Auth-User': username,
          'X-Auth-Pass': password,
          'Content-Type': 'application/json'
        },
        credentials: 'omit'
      });

      console.log('Método 1 (X-Auth) - Response status:', response.status);

      // ...existing fallback methods...

      // Si el método 1 falla, intentar método 2 con GET params
      if (response.status === 401) {
        console.log('Método 1 falló, probando método 2 con GET params...');

        const url = `/api-stats.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&date_from=${fromDate}&date_to=${toDate}`;
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'omit'
        });

        console.log('Método 2 (GET params) - Response status:', response.status);

        // Si el método 2 falla, intentar método 3 con POST JSON
        if (response.status === 401) {
          console.log('Método 2 falló, probando método 3 con POST JSON...');

          response = await fetch('/api-stats.php', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              username: username,
              password: password,
              date_from: fromDate,
              date_to: toDate
            }),
            credentials: 'omit'
          });

          console.log('Método 3 (POST JSON) - Response status:', response.status);

          // Si el método 3 falla, intentar método 4 con Authorization header
          if (response.status === 401) {
            console.log('Método 3 falló, probando método 4 con Authorization header...');

            response = await fetch(`/api-stats.php?date_from=${fromDate}&date_to=${toDate}`, {
              method: 'GET',
              headers: {
                'Authorization': `Basic ${authCredentials}`,
                'Content-Type': 'application/json'
              },
              credentials: 'omit'
            });

            console.log('Método 4 (Authorization) - Response status:', response.status);
          }
        }
      }

      if (response.status === 401) {
        const errorData = await response.json();
        console.log('Error 401 data:', errorData);
        setError('❌ Credenciales incorrectas. Haga clic en "Reintentar" para ingresar nuevas credenciales.');
        return;
      }

      if (!response.ok) {
        setError(`❌ Error del servidor: ${response.status}`);
        return;
      }

      const result = await response.json();
      console.log('Response data:', result);

      if (result.success && result.data) {
        setData(result.data);
        setAnalytics(result.analytics || null);
        setProductData(result.analytics?.product_stats || []);
        setOriginData(result.analytics?.origin_stats || []);
      } else {
        setError(result.error || '❌ Error desconocido al cargar datos');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('❌ Error de conexión: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setCredentials(null);
    requestCredentials();
  };

  const handleDateFilterChange = () => {
    if (credentials) {
      fetchStats(credentials, dateFrom, dateTo);
    }
  };

  // Colores para los gráficos de pie
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  const totalClicks = analytics?.total_clicks || data.reduce((sum, item) => sum + item.value, 0);
  const avgClicksPerDay = data.length > 0 ? Math.round(totalClicks / data.length) : 0;

  return (
    <>
      <DefaultNavbarLayout />

      <MKBox
        minHeight="100vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.9),
              rgba(gradients.dark.state, 0.9)
            )}`,
          backgroundSize: "cover",
          backgroundPosition: "50%",
          display: "grid",
          placeItems: "center",
          py: 12
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12}>


              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                  <br />
                  <button
                    onClick={handleRetry}
                    style={{
                      marginTop: '10px',
                      padding: '8px 16px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Reintentar
                  </button>
                </Alert>
              )}

              {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress size={60} sx={{ color: 'white' }} />
                  <MKTypography variant="h6" ml={2} color="white">
                    Cargando estadísticas...
                  </MKTypography>
                </Box>
              )}

              {!loading && !error && data.length > 0 && (
                <>
                  <Card
                    sx={{
                      backgroundColor: ({ palette: { dark }, functions: { rgba } }) =>
                        rgba(dark.main, 0.95),
                      backdropFilter: "saturate(200%) blur(30px)",
                      boxShadow: ({ boxShadows: { xxl } }) => xxl,
                      color: 'white'
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <MKBox textAlign="center" mb={4}>
                        <MKTypography variant="h3" fontWeight="bold" color="white">
                          📊 Dashboard de Estadísticas
                        </MKTypography>
                        <MKTypography variant="body1" color="white" opacity={0.8} mt={1}>
                          Análisis de clics con filtros de fecha
                        </MKTypography>
                        
                        {/* Filtros de fecha */}
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            mt: 3,
                            mb: 2,
                            flexWrap: 'wrap'
                          }}
                        >
                          <TextField
                            label="Fecha desde"
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                                '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
                                '& input': { 
                                  color: 'white !important',
                                  colorScheme: 'dark'
                                },
                                '& input[type="date"]': {
                                  color: 'white !important',
                                  colorScheme: 'dark'
                                },
                                '& input::-webkit-calendar-picker-indicator': {
                                  filter: 'invert(1)',
                                  cursor: 'pointer'
                                }
                              },
                              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                            }}
                          />
                          <TextField
                            label="Fecha hasta"
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                                '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
                                '& input': { 
                                  color: 'white !important',
                                  colorScheme: 'dark'
                                },
                                '& input[type="date"]': {
                                  color: 'white !important',
                                  colorScheme: 'dark'
                                },
                                '& input::-webkit-calendar-picker-indicator': {
                                  filter: 'invert(1)',
                                  cursor: 'pointer'
                                }
                              },
                              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                            }}
                          />
                          <button
                            onClick={handleDateFilterChange}
                            style={{
                              padding: '10px 20px',
                              backgroundColor: '#2196f3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            🔍 Filtrar
                          </button>
                        </Box>
                      </MKBox>
                      {/* Estadísticas resumidas */}
                      <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} md={6}>
                          <Card sx={{
                            backgroundColor: 'rgba(33, 150, 243, 0.8)',
                            color: 'white',
                            backdropFilter: "blur(10px)"
                          }}>
                            <CardContent sx={{ textAlign: 'center', py: 3 }}>
                              <MKTypography variant="h4" color="white" fontWeight="bold">
                                {totalClicks}
                              </MKTypography>
                              <MKTypography variant="body2" color="white" opacity={0.9}>
                                Total de clics
                              </MKTypography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card sx={{
                            backgroundColor: 'rgba(76, 175, 80, 0.8)',
                            color: 'white',
                            backdropFilter: "blur(10px)"
                          }}>
                            <CardContent sx={{ textAlign: 'center', py: 3 }}>
                              <MKTypography variant="h4" color="white" fontWeight="bold">
                                {avgClicksPerDay}
                              </MKTypography>
                              <MKTypography variant="body2" color="white" opacity={0.9}>
                                Promedio por día
                              </MKTypography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Gráfico */}
                      <Card sx={{
                        p: 3,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        backdropFilter: "blur(10px)",
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <MKTypography variant="h5" mb={3} textAlign="center" color="white">
                          📈 Clics por día
                        </MKTypography>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart
                            data={data}
                            margin={{
                              top: 20,
                              right: 30,
                              left: 20,
                              bottom: 60,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.2)" />
                            <XAxis
                              dataKey="label"
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              tick={{ fill: 'white', fontSize: 12 }}
                            />
                            <YAxis tick={{ fill: 'white', fontSize: 12 }} />
                            <Tooltip
                              labelStyle={{ color: 'white' }}
                              itemStyle={{ color: 'white' }}
                              contentStyle={{
                                backgroundColor: 'rgba(0,0,0,0.9)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '8px',
                                color: 'white'
                              }}
                              formatter={(value) => [`${value} clics`]}
                            />
                            <Legend wrapperStyle={{ color: 'white' }} />
                            <Bar
                              dataKey="value"
                              fill="#2196f3"
                              name="Clics"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>

                      {/* Gráficos de participación */}
                      <Grid container spacing={3} mt={2}>
                        {/* Gráfico de Products */}
                        <Grid item xs={12} md={6}>
                          <Card sx={{
                            p: 3,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            backdropFilter: "blur(10px)",
                            border: '1px solid rgba(255,255,255,0.1)'
                          }}>
                            <MKTypography variant="h6" mb={3} textAlign="center" color="white">
                              🎯 Participación por Producto
                            </MKTypography>
                            {productData.length > 0 ? (
                              <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                  <Pie
                                    data={productData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ product, percentage }) => `${product}: ${percentage}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="count"
                                  >
                                    {productData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip 
                                    contentStyle={{ 
                                      backgroundColor: 'rgba(0,0,0,0.9)',
                                      border: '1px solid rgba(255,255,255,0.2)',
                                      borderRadius: '8px',
                                      color: 'white'
                                    }}
                                    itemStyle={{ color: 'white' }}
                                    labelStyle={{ color: 'white' }}
                                    formatter={(value, name, props) => [
                                      `${value} clics`,
                                      `${props.payload.product}`
                                    ]}
                                    labelFormatter={() => 'Estadísticas del Producto'}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <Box textAlign="center" py={4}>
                                <MKTypography variant="body2" color="white" opacity={0.7}>
                                  No hay datos de productos disponibles
                                </MKTypography>
                              </Box>
                            )}
                          </Card>
                        </Grid>

                        {/* Gráfico de Origin */}
                        <Grid item xs={12} md={6}>
                          <Card sx={{
                            p: 3,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            backdropFilter: "blur(10px)",
                            border: '1px solid rgba(255,255,255,0.1)'
                          }}>
                            <MKTypography variant="h6" mb={3} textAlign="center" color="white">
                              📱 Participación por Origen
                            </MKTypography>
                            {originData.length > 0 ? (
                              <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                  <Pie
                                    data={originData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ origin, percentage }) => `${origin}: ${percentage}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="count"
                                  >
                                    {originData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip 
                                    contentStyle={{ 
                                      backgroundColor: 'rgba(0,0,0,0.9)',
                                      border: '1px solid rgba(255,255,255,0.2)',
                                      borderRadius: '8px',
                                      color: 'white'
                                    }}
                                    itemStyle={{ color: 'white' }}
                                    labelStyle={{ color: 'white' }}
                                    formatter={(value, name, props) => [
                                      `${value} clics`,
                                      `${props.payload.origin}`
                                    ]}
                                    labelFormatter={() => 'Estadísticas del Origen'}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <Box textAlign="center" py={4}>
                                <MKTypography variant="body2" color="white" opacity={0.7}>
                                  No hay datos de origen disponibles
                                </MKTypography>
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      </Grid>
                      <MKBox textAlign="center" mt={3}>
                        <MKTypography variant="body2" color="white" opacity={0.7}>
                          🕒 Última actualización: {new Date().toLocaleString('es-AR')}
                        </MKTypography>
                        <MKTypography variant="body2" color="white" opacity={0.5} mt={1}>
                          🔒 Panel de administración - Acceso restringido
                        </MKTypography>
                      </MKBox>
                    </CardContent>
                  </Card>
                </>
              )}

              {!loading && !error && data.length === 0 && (
                <Alert severity="info" sx={{ textAlign: 'center' }}>
                  📝 No hay datos disponibles para mostrar.
                </Alert>
              )}


            </Grid>
          </Grid>
        </Container>
      </MKBox>

      <Footer />
    </>
  );
}

export default AdminDashboard;
